import React from "react";
import { Link } from "react-router-dom";

import "../../styles/Seo.css";
import SeoMetaTags, { SITE_ORIGIN } from "../SeoMetaTags";

export const OMAHA_PATH = "/omaha";
export const GUIDE_PATH = "/guide";
// "Pot Limit Omaha Equity Calculator" in the h1 covers three of the four target
// phrases at once, since it contains "omaha equity calculator" as a substring.
export const OMAHA_TITLE =
  "Omaha Equity Calculator - Free PLO Odds Calculator";
export const OMAHA_DESCRIPTION =
  "Free PLO equity calculator. Deal four-card hands to up to nine players, add a board, and get pot limit Omaha equity instantly. 100,000-trial simulation.";

export const GUIDE_TITLE =
  "Omaha & Hold'em Equity Guide - How the Odds Are Calculated";
export const GUIDE_DESCRIPTION =
  "How poker equity is calculated: Omaha's two-hole-card rule, why PLO ranges cannot be listed like Hold'em, simulation accuracy, and answers to common Omaha odds questions.";

// Every number here is measured, not marketing: the combo counts come from
// enumerating the deck, the accuracy figures from the equity service itself.
export const OMAHA_FAQ = [
  {
    q: "How are Omaha odds calculated?",
    a:
      "This calculator runs a 100,000-hand Monte Carlo simulation. It deals the remaining board cards at random tens of thousands of times, scores every player's best five-card hand, and reports how often each one wins or ties. At 100,000 trials the result lands within about 0.13 percentage points of the true equity, which is far tighter than any decision you would make at the table."
  },
  {
    q: "Why do you have to use exactly two hole cards in Omaha?",
    a:
      "Omaha's defining rule is that your hand is exactly two of your four hole cards plus exactly three of the five board cards. You cannot play one card or all four. That is why a single ace in your hand does not make a flush when four of that suit hit the board, and it is the single most common mistake new Omaha players make. Every showdown is really the best of 60 combinations: six pairs of hole cards times ten sets of three board cards."
  },
  {
    q: "How many starting hands are there in Omaha?",
    a:
      "There are 270,725 possible four-card combinations, which collapse to 16,432 strategically distinct starting hands once you ignore which suit is which. Texas Hold'em has 1,326 combinations and 169 distinct hands, so Omaha's starting-hand space is roughly 97 times larger. That is why Omaha ranges are described rather than listed hand by hand."
  },
  {
    q: "Is a pair of aces always the best hand before the flop in Omaha?",
    a:
      "No. Aces matter far less in Omaha than in Hold'em because every opponent holds four cards and connects with the board more often. Double-suited aces with connected side cards are genuinely strong, but bare aces with two disconnected offsuit cards run close to a coin flip against a good rundown. Deal both hands into the calculator and you will usually see the gap is much smaller than you expect."
  },
  {
    q: "Why are Omaha equities so much closer together than Hold'em equities?",
    a:
      "With four hole cards each, every player has six two-card combinations working for them instead of one. More combinations mean more ways to connect with any board, so hands run closer together. Preflop matchups that would be 80/20 in Hold'em are often 60/40 in Omaha, which is why pot-limit betting and equity realization matter so much."
  },
  {
    q: "What is a dangler in Omaha?",
    a:
      "A dangler is a fourth card that does not work with the other three, like the deuce in A-K-Q-2. It effectively reduces your hand to three useful cards and cuts the number of playable two-card combinations you hold, which is why hands with a dangler perform worse than their high cards suggest."
  },
  {
    q: "Does this calculator support Omaha hi-lo (8 or better)?",
    a:
      "Not yet. It calculates high-only Omaha equity, which covers pot-limit Omaha as it is normally spread. Hi-lo split pots are not scored."
  }
];

function faqJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: OMAHA_FAQ.map(item => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a }
    }))
  };
}

function appJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Pot Limit Omaha Equity Calculator",
    alternateName: ["PLO Equity Calculator", "Omaha Odds Calculator"],
    url: SITE_ORIGIN + OMAHA_PATH,
    applicationCategory: "GameApplication",
    operatingSystem: "Any",
    browserRequirements: "Requires JavaScript",
    description: OMAHA_DESCRIPTION,
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    featureList: [
      "Exact four-card hands for up to nine players",
      "Flop, turn and river board input",
      "100,000-trial Monte Carlo equity simulation",
      "Shareable calculation links"
    ]
  };
}

export function OmahaMetaTags() {
  return (
    <SeoMetaTags
      title={OMAHA_TITLE}
      description={OMAHA_DESCRIPTION}
      path={OMAHA_PATH}
      jsonLd={[appJsonLd()]}
    />
  );
}

// The FAQ markup lives on the guide, so its structured data does too —
// schema.org data has to describe what is visible on the same page.
export function GuideMetaTags() {
  return (
    <SeoMetaTags
      title={GUIDE_TITLE}
      description={GUIDE_DESCRIPTION}
      path={GUIDE_PATH}
      jsonLd={[faqJsonLd()]}
    />
  );
}

// On the tool page: what the page is, and how to drive it.
export default function OmahaSeoContent(props) {
  return (
    <section className="seoArticle">
      <h1>Pot Limit Omaha Equity Calculator</h1>
      <p className="seoLede">
        Work out pot limit Omaha equity for any matchup. This PLO equity
        calculator deals four hole cards to each player, takes a flop, turn or
        river, and shows who is ahead and by how much — free, instant, and
        without an account.
      </p>

      <h2>How to use the PLO equity calculator</h2>
      <ol>
        <li>
          Pick a player, then click four cards in the deck to give them a hand.
          Cards already dealt to another player, or sitting on the board, are
          greyed out, so you cannot build an impossible hand.
        </li>
        <li>
          Add more players with <strong>Add player</strong> — up to nine seats.
        </li>
        <li>
          Type a board if you want equity after the flop, turn or river. Leave it
          empty for preflop equity.
        </li>
        <li>
          Press <strong>Calculate</strong>. Each player's share of the pot is
          shown as a percentage bar.
        </li>
      </ol>
      <p>
        <Link to={GUIDE_PATH}>Read the Omaha equity guide</Link> for the
        two-hole-card rule, how the odds are calculated, and answers to common
        Omaha questions. For two-card games, use the{" "}
        <Link to="/">poker hand equity calculator</Link>.
      </p>
    </section>
  );
}

// On the guide: the explanatory depth, which is what a search engine can rank
// and what the competing calculators do not publish at all.
export function OmahaGuideSections(props) {
  return (
    <section className="seoArticle">
      <h2>The Omaha rule that changes every calculation</h2>
      <p>
        In Omaha you play exactly two of your four hole cards and exactly three
        of the five board cards — never one, never all four. A lone ace in your
        hand is not a flush when three of that suit appear on the board, and a
        single connecting card cannot complete a straight on its own. Each
        showdown is the best of 60 combinations: six pairs of hole cards
        multiplied by ten sets of three board cards. The{" "}
        <Link to={OMAHA_PATH}>Omaha odds calculator</Link> scores all 60 for
        every player on every simulated board, so the number you see already
        accounts for the rule that trips most players up.
      </p>

      <h2>Why Omaha ranges cannot be listed like Hold'em ranges</h2>
      <p>
        Four hole cards give 270,725 possible combinations, or 16,432 distinct
        starting hands once you ignore which suit is which. Hold'em has 1,326
        combinations and 169 distinct hands — the reason its 13x13 grid works at
        all. Omaha's starting-hand space is about 97 times larger, so there is no
        grid to click. That is why the Omaha calculator starts from exact hands:
        the deck is the honest control when a hand is four specific cards.
      </p>

      <h2>How accurate are the results?</h2>
      <p>
        Equity is estimated by simulation rather than by enumerating every
        possible runout, because a single Omaha matchup can span billions of
        board-and-hand combinations. Each calculation runs 100,000 trials, which
        puts the answer within roughly 0.13 percentage points of the true
        equity. Run the same matchup twice and the last decimal may move; the
        decision it supports will not.
      </p>

      <h2>Omaha odds questions</h2>
      <dl className="seoFaq">
        {OMAHA_FAQ.map(item => (
          <div key={item.q}>
            <dt>{item.q}</dt>
            <dd>{item.a}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
